import { registerPlugin } from "@capacitor/core";
import type { ProductId, ProductKind, PurchaseResult, Store, StoreEnvironment, StoreProduct, StoreTransaction } from "@highmarch/app";

/**
 * The iOS shell's `Store` (FINISH_PLAN D9, ADR 0013): StoreKit 2 through the app's own Capacitor plugin
 * (ios/App/App/StoreKitPlugin.swift, registered as `HighmarchStoreKit` by GameViewController.swift). The native side does
 * the StoreKit work — `Product.products`, `product.purchase()`, `Transaction.updates`, `Transaction.unfinished`,
 * `AppStore.sync`, `Transaction.currentEntitlements`, `transaction.finish()` — and checks every transaction's JWS on
 * the device (`VerificationResult.verified`); an unverified one never crosses the bridge. This side validates the
 * shape of what does cross (anything read from outside the game is validated, CLAUDE.md) and drops the rest.
 */

/** A transaction as the plugin sends it. */
interface NativeTransaction {
  transactionId?: unknown; originalTransactionId?: unknown; productId?: unknown; quantity?: unknown;
  purchasedAtMs?: unknown; environment?: unknown; revokedAtMs?: unknown; jws?: unknown;
}

/** The plugin's calls (StoreKitPlugin.swift `pluginMethods`); results are objects, as the bridge requires. */
export interface StoreKitBridge {
  canPay(): Promise<{ value: boolean }>;
  products(o: { ids: string[] }): Promise<{ products: Array<{ id?: unknown; kind?: unknown; displayName?: unknown; displayPrice?: unknown }> }>;
  purchase(o: { id: string }): Promise<{ status?: unknown; transaction?: NativeTransaction; reason?: unknown }>;
  unfinished(): Promise<{ transactions: NativeTransaction[] }>;
  finish(o: { transactionId: string }): Promise<void>;
  restore(): Promise<{ transactions: NativeTransaction[] }>;
  addListener(event: "transaction", listener: (t: NativeTransaction) => void): Promise<{ remove(): Promise<void> }>;
  /** `Storefront.current?.countryCode` (ISO 3166-1 alpha-3); absent when StoreKit has none (docs/SHOP.md flag 1). */
  storefront(): Promise<{ countryCode?: unknown }>;
}

/** A storefront country code as StoreKit gives it: three capital letters (ISO 3166-1 alpha-3). */
const COUNTRY_ALPHA3 = /^[A-Z]{3}$/;
const ENVIRONMENTS: readonly StoreEnvironment[] = ["sandbox", "xcode", "appStoreSandbox", "production"];
const KINDS: readonly ProductKind[] = ["consumable", "nonConsumable"];

/** A verified transaction's fields, checked; null when anything is missing or of the wrong type. */
export function parseNativeTransaction(t: NativeTransaction | null | undefined): StoreTransaction | null {
  if (!t) return null;
  const idOk = (v: unknown): v is string => typeof v === "string" && v.length > 0;
  const msOk = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
  if (!idOk(t.transactionId) || !idOk(t.originalTransactionId) || !idOk(t.productId)) return null;
  if (!Number.isInteger(t.quantity) || (t.quantity as number) < 1 || !msOk(t.purchasedAtMs)) return null;
  if (!ENVIRONMENTS.includes(t.environment as StoreEnvironment)) return null;
  if (t.revokedAtMs !== undefined && t.revokedAtMs !== null && !msOk(t.revokedAtMs)) return null;
  if (t.jws !== undefined && typeof t.jws !== "string") return null;
  return {
    transactionId: t.transactionId, originalTransactionId: t.originalTransactionId, productId: t.productId,
    quantity: t.quantity as number, purchasedAtMs: t.purchasedAtMs, environment: t.environment as StoreEnvironment,
    ...(msOk(t.revokedAtMs) ? { revokedAtMs: t.revokedAtMs } : {}),
    ...(typeof t.jws === "string" ? { jws: t.jws } : {}),
  };
}

export class StoreKitStore implements Store {
  readonly kind = "storekit2" as const;
  private readonly listeners: Array<(t: StoreTransaction) => void> = [];
  private subscribed = false;

  constructor(private readonly bridge: StoreKitBridge = registerPlugin<StoreKitBridge>("HighmarchStoreKit"), private readonly log?: (message: string) => void) {}

  async canPay(): Promise<boolean> {
    try { return (await this.bridge.canPay()).value === true; } catch { return false; }
  }

  async products(ids: readonly ProductId[]): Promise<readonly StoreProduct[]> {
    const got = (await this.bridge.products({ ids: [...ids] })).products ?? [];
    const valid: StoreProduct[] = [];
    for (const p of got) {
      if (typeof p.id !== "string" || !KINDS.includes(p.kind as ProductKind) || typeof p.displayName !== "string" || typeof p.displayPrice !== "string") continue;
      valid.push({ id: p.id, kind: p.kind as ProductKind, displayName: p.displayName, displayPrice: p.displayPrice });
    }
    // In the order asked, as the port promises (StoreKit returns them in any order).
    return ids.flatMap((id) => valid.filter((p) => p.id === id));
  }

  async purchase(id: ProductId): Promise<PurchaseResult> {
    let r: Awaited<ReturnType<StoreKitBridge["purchase"]>>;
    try { r = await this.bridge.purchase({ id }); } catch (err) { return { status: "failed", reason: (err as Error)?.message ?? String(err) }; }
    switch (r.status) {
      case "purchased": {
        const tx = parseNativeTransaction(r.transaction);
        return tx ? { status: "purchased", transaction: tx } : { status: "failed", reason: "the store returned a malformed transaction" };
      }
      case "pending": return { status: "pending" };
      case "cancelled": return { status: "cancelled" };
      default: return { status: "failed", reason: typeof r.reason === "string" ? r.reason : "the store did not complete the purchase" };
    }
  }

  async unfinished(): Promise<readonly StoreTransaction[]> { return this.parseList((await this.bridge.unfinished()).transactions); }
  finish(transactionId: string): Promise<void> { return this.bridge.finish({ transactionId }); }
  async restore(): Promise<readonly StoreTransaction[]> { return this.parseList((await this.bridge.restore()).transactions); }

  /** The App Store storefront's country, or null when StoreKit has none or the plugin is older (no `storefront`). */
  async storefront(): Promise<string | null> {
    try {
      const code = (await this.bridge.storefront()).countryCode;
      return typeof code === "string" && COUNTRY_ALPHA3.test(code) ? code : null;
    } catch { return null; }
  }

  onTransaction(listener: (t: StoreTransaction) => void): () => void {
    this.listeners.push(listener);
    if (!this.subscribed) {
      this.subscribed = true;
      void this.bridge.addListener("transaction", (raw) => {
        const tx = parseNativeTransaction(raw);
        if (!tx) { this.log?.("storekit: dropped a malformed transaction update"); return; }
        for (const l of [...this.listeners]) l(tx);
      });
    }
    return () => { const i = this.listeners.indexOf(listener); if (i >= 0) this.listeners.splice(i, 1); };
  }

  private parseList(list: NativeTransaction[] | undefined): StoreTransaction[] {
    const out: StoreTransaction[] = [];
    for (const raw of list ?? []) {
      const tx = parseNativeTransaction(raw);
      if (tx) out.push(tx); else this.log?.("storekit: dropped a malformed transaction");
    }
    return out;
  }
}
